import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import { useDiRef } from '@/common/react/di-hooks.ts'
import type { CollectionViewsService } from '@/objects/services/CollectionViewsService.ts'
import { useBehaviorObservable, useObservable } from '@/common/react/observable-hooks.ts'
import type { CollectionsTreeService } from '@/objects/services/CollectionsTreeService.ts'
import type { CollectionModel } from '@/core/gql/gql-models-override.ts'
import { CollectionTabs } from './CollectionTabs.tsx'
import { CollectionGrid } from 'objects/collection-view/grid/CollectionGrid.tsx'
import { CollectionViewSettingsButtons } from '@/objects/collection-view/top-bar/CollectionViewSettingsButtons.tsx'
import { skip, take } from 'rxjs'
import { CollectionName } from '@/objects/collection-view/top-bar/CollectionName.tsx'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'
import { CollectionViewPageContext } from '@/objects/collection-view/top-bar/CollectionViewPageContext.ts'

export interface CollectionPageOpenData {
  fromApp: boolean
}

export function CollectionPage() {
  const location = useLocation()
  const service = useDiRef<CollectionViewsService>('CollectionViewsService')
  const typesService = useDiRef<ObjectTypesService>('ObjectTypesService')
  const treeService = useDiRef<CollectionsTreeService>('CollectionsTreeService')
  const params = new URLSearchParams(location.search)
  const id = params.get('id')!
  const isPageReload =
    (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
      ?.type === 'reload'
  const isFromNavigation =
    !isPageReload && ((location.state as CollectionPageOpenData | undefined)?.fromApp ?? false)

  const collectionTreeFirstVersion$ = useMemo(
    () => treeService.collectionsTreeVersion$.pipe(skip(1), take(1)),
    [],
  )
  useObservable(collectionTreeFirstVersion$, () => treeService.collectionsTreeVersion$.value)
  useBehaviorObservable(typesService.selectedSpaceTypes$)
  const viewSettings = useBehaviorObservable(service.getViewSettings$(id))
  service.fetchIfNotFetched(id)

  const collectionTree = treeService.collectionsTree
  let collection: CollectionModel | undefined

  if (collectionTree) {
    collection = collectionTree.idToNode.get(id)! as CollectionModel
  } else if (!isFromNavigation && service.localStorageState) {
    collection = {
      id,
      typeId: service.localStorageState.lastOpenedCollectionTypeId,
    } as CollectionModel
  }

  useEffect(() => {
    if (!collection) {
      return
    }
    service.localStorageState = {
      lastOpenedCollectionId: collection.id,
      lastOpenedCollectionTypeId: collection.typeId,
    }
  }, [collection])

  const typeId = collection?.typeId
  const objectType = typeId ? typesService.getTypeFromSelectedSpace(typeId) : undefined
  return (
    <CollectionViewPageContext.Provider
      value={{ collectionId: id, collection, objectType, viewSettings }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <CollectionName />
          <CollectionViewSettingsButtons />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            px: 1,
            flexShrink: 0,
          }}
        >
          <CollectionTabs />
        </Box>

        <CollectionGrid />
      </Box>
    </CollectionViewPageContext.Provider>
  )
}
